import React, {useState, useEffect, useRef} from "react";
import { PriceHistoryOverviewCard } from "./price_control/PriceHistoryOverviewCard";
import { StoreComparisonCard } from "./price_control/StoreComparisonCard";
import { PriceEvolutionChartCard } from "./price_control/PriceEvolutionChartCard";
import { CheapestStoresTableCard } from "./price_control/CheapestStoresChartCard";
import { ItemPriceHistoryCard } from "./price_control/ItemPriceHistoryCard";
import {Filter, X, Search} from "lucide-react";
import apiFetch from './apiClient';

const PRODUCT_COLOR_PALETTE = [
    "var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)",
    "var(--chart-5)", "var(--chart-6)", "var(--chart-7)", "var(--chart-8)",
];

const PriceControl = () => {

    const [priceHistoryMetrics, setPriceHistoryMetrics] = useState(null);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [storeComparisonData, setStoreComparisonData] = useState([]);
    const [priceEvolutionData, setPriceEvolutionData] = useState([]);
    const [cheapestStoresData, setCheapestStoresData] = useState([]);
    const [itemPriceHistoryData, setItemPriceHistoryData] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [timeRange, setTimeRange] = useState('6');
    const [searchQuery, setSearchQuery] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    const filterProducts = availableProducts.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !selectedProducts.includes(product.id)
    );

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [priceHistoryResponse, myProductsResponse, cheapestStoresResponse, itemPriceHistoryResponse] = await Promise.all([
                    apiFetch('/api/pricecontrol/price-history-metrics'),
                    apiFetch('/api/pricecontrol/my-products'),
                    apiFetch('/api/pricecontrol/cheapest-stores'),
                    apiFetch('/api/pricecontrol/item-price-history')
                ]);
                setPriceHistoryMetrics(priceHistoryResponse);
                setAvailableProducts((myProductsResponse.data || []).map((p, index) => ({
                    id: p.produto_id,
                    name: p.produto_nome,
                    color: PRODUCT_COLOR_PALETTE[index % PRODUCT_COLOR_PALETTE.length]
                })));
                setCheapestStoresData(cheapestStoresResponse.data || []);
                setItemPriceHistoryData(itemPriceHistoryResponse.data || []);
            } catch (error) {
                console.error('Error fetching price control data:', error);
            }
        };
        fetchData();

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const fetchComparisons = async () => {
            if (selectedProducts.length === 0) {
                setStoreComparisonData([]);
                setPriceEvolutionData([]);
                return;
            }
            const idsParam = selectedProducts.join(',');
            const monthsParam = timeRange === 'all' ? '' : `&months=${timeRange}`;
            try {
                const [storeComparisonResponse, priceEvolutionResponse] = await Promise.all([
                    apiFetch(`/api/pricecontrol/store-comparison?produto_ids=${idsParam}`),
                    apiFetch(`/api/pricecontrol/price-evolution?produto_ids=${idsParam}${monthsParam}`)
                ]);
                setStoreComparisonData(storeComparisonResponse.data || []);
                setPriceEvolutionData(priceEvolutionResponse.data || []);
            } catch (error) {
                console.error('Error fetching price comparison data:', error);
            }
        };
        fetchComparisons();
    }, [selectedProducts, timeRange]);

    const addProduct = (productId) => {
        if (!selectedProducts.includes(productId)) {
            setSelectedProducts(prev => [...prev, productId]);
        }
        setSearchQuery('');
        setShowDropdown(false);
        inputRef.current?.focus();
    };

    const removeProduct = (productId) => {
        setSelectedProducts(prev => prev.filter(id => id !== productId));
    }

    return (
        <div className="min-vh-100 p-4">
            <div className="container-fluid" style={{ maxWidth: '1400px' }}>
                {/* Analytic Grid */}
                <div className="row g-4">
                    <div className="col-12 col-lg-12">
                        <PriceHistoryOverviewCard
                            priceHistoryMetrics = {priceHistoryMetrics}
                        />
                    </div>

                    <div className="col-12">
                    <button
                        className="btn btn-outline-custom d-flex align-items-center gap-2"
                        onClick={() => setShowFilter(!showFilter)}
                    >
                        <Filter size={16} />
                        Filtrar Produtos ({selectedProducts.length})
                    </button>
                    {showFilter && (
                        <div className="custom-card mt-3 p-4">
                            <h5 className="mb-3">Selecione os produtos para comparar</h5>

                            <div className="mb-3 position-relative" ref={dropdownRef}>
                                <div className="input-group">
                                    <span className="input-group-text badge-secondary-custom">
                                        <Search size={16} />
                                    </span>
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        className="form-control badge-secondary-custom"
                                        placeholder="Buscar produtos..."
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setShowDropdown(true);
                                        }}
                                        onFocus={() => setShowDropdown(true)}
                                    />
                                </div>
                                {showDropdown && filterProducts.length > 0 && (
                                    <div className="position-absolute w-100 mt-1 custom-card product-filter-custom">
                                        {filterProducts.map(product => (
                                            <div
                                                key={product.id}
                                                role="button"
                                                tabIndex={0}
                                                className="p-3 d-flex align-items-center gap-2 show-dropdown-custom"
                                                onClick={() => addProduct(product.id)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        addProduct(product.id);
                                                    }
                                                }}
                                                onMouseEnter={(e)=>{e.currentTarget.style.backgroundColor='rgba(37, 42, 58, 0.5)'}}
                                                onMouseLeave={(e)=>{e.currentTarget.style.backgroundColor='transparent'}}
                                            >
                                                <div className="chart-legend-dot" style={{backgroundColor: product.color}} />
                                                <span className="">{product.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {showDropdown && searchQuery && filterProducts.length === 0 && (
                                    <div className="position-absolute w-100 mt-1 custom-card product-filter-custom p-3">
                                        <span className="">Nenhum produto encontrado.</span>
                                    </div>
                                )}
                            </div>

                            {selectedProducts.length > 0 && (
                                <div>
                                    <label className="form-label text-muted-custom small mb-2">Produtos Selecionados: ({selectedProducts.length})</label>
                                    <div className="d-flex flex-wrap gap-2">
                                        {selectedProducts.map(productId => {
                                            const product = availableProducts.find(p => p.id === productId);
                                            return product ? (
                                                <div
                                                    key={product.id}
                                                    className="badge bg-secundary-custom d-flex align-items-center gap-2 py-2 px-3 chart-legend-item"
                                                >
                                                    <div className="selected-product-card-custom" style={{backgroundColor: product.color}} />
                                                    <span>{product.name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={()=>removeProduct(productId)}
                                                        className="btn btn-link p-0 ms-1 btn-selected-product-custom"
                                                        onMouseEnter={(e)=>{e.currentTarget.style.color='var(--color-text-primary)'}}
                                                        onMouseLeave={(e)=>{e.currentTarget.style.color='var(--color-text-secondary)'}}
                                                        aria-label={`Remover ${product.name}`}
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ) : null;
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    </div>

                    <div className="col-12 col-lg-12">
                        <StoreComparisonCard
                            selectedProducts={selectedProducts}
                            storeComparisonData={storeComparisonData}
                        />
                    </div>
                    <div className="col-12">
                        <PriceEvolutionChartCard
                            selectedProducts={selectedProducts}
                            availableProducts={availableProducts}
                            priceEvolutionData={priceEvolutionData}
                            timeRange={timeRange}
                            onTimeRangeChange={setTimeRange}
                        />
                    </div>
                    <div className="col-12">
                        <CheapestStoresTableCard cheapestStoresData={cheapestStoresData} />
                    </div>
                    <div className="col-12">
                        <ItemPriceHistoryCard itemPriceHistoryData={itemPriceHistoryData} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PriceControl;
